import { useState, type FormEvent } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSpeciesCatalog } from './useSpeciesCatalog'

// person/vehicle/animal are structural rows — listed for context, never removable here.
const STRUCTURAL = new Set(['coarse', 'fallback'])

/**
 * Superuser UI to manage the open-vocabulary species catalog (#108). Adding a
 * species expands what BioCLIP can name across every camera using this model.
 */
export function SpeciesCatalogManager({ baseModelId }: { baseModelId: number }) {
  const { t } = useTranslation('detectionAccuracy')
  const cat = useSpeciesCatalog(baseModelId)
  const [name, setName] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const v = name.trim()
    if (!v) return
    cat.add(v, { onSuccess: () => setName('') })
  }

  const species = cat.species.filter((c) => !STRUCTURAL.has(c.category))
  const structural = cat.species.filter((c) => STRUCTURAL.has(c.category))

  return (
    <div className="rounded-lg border border-border">
      <div className="border-b border-border px-4 py-3">
        <h2 className="font-medium">{t('catalog.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('catalog.subtitle')}</p>
      </div>
      <div className="space-y-3 px-4 py-3">
        {cat.isLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : cat.isError ? (
          <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
            <span>{t('loadError')}</span>
            <button type="button" onClick={() => cat.refetch()} className="text-primary underline">{t('retry')}</button>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border/60">
              {species.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2">
                  <span className="text-sm">{c.class_name}</span>
                  <button
                    type="button"
                    aria-label={t('catalog.remove')}
                    disabled={cat.removing}
                    onClick={() => { if (window.confirm(t('catalog.removeConfirm', { name: c.class_name }))) cat.remove(c.id) }}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-40"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
              {species.length === 0 && (
                <li className="py-2 text-sm text-muted-foreground">{t('catalog.empty')}</li>
              )}
            </ul>

            <form onSubmit={submit} className="flex items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('catalog.addPlaceholder')}
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="submit"
                disabled={cat.adding || !name.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> {t('catalog.add')}
              </button>
            </form>

            {structural.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('catalog.structuralNote', { names: structural.map((c) => c.class_name).join(', ') })}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
